import {
  Box,
  Button,
  Flex,
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@100mslive/hms-ai-ui";
import {
  ArrowLeft,
  NavArrowDown,
  Phone,
  PhoneOutcome,
  WrapText,
} from "iconoir-react";
import { cloneDeep, isEmpty, set } from "lodash-es";
import moment from "moment";
import { default as React, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { AppDispatch, RootState } from "src/store";

import callImg from "../../../assets/journeys/call.png";
import engagementImg from "../../../assets/journeys/engagement.png";
import { APIStateEnum } from "../../../common/api/states";
import { postDemoCall, postDemoEngagement } from "../../../services/demo";
import { fetchJourneys } from "../../../services/journeys";
import { resetCallsState } from "../../../store/calls/store";
import {
  type ActivityType,
  resetDemoState,
  setCallOptions,
} from "../../../store/demo/store";
import { setJourney } from "../../../store/journeys/store";
import { getCompleteTimeZone } from "../../../utils/time";
import JourneyContext from "../components/JourneyContext";
import {
  availableDateSlotsConfig,
  generateTimeSlots,
} from "../components/opinionated/foodsmart/availableslotconfig";
import { generateWeeklyDateTimeSlots } from "../components/opinionated/foodsmart/multiDateSelectFoodSmart";
import { GetFieldForForm } from "../utils";
import { FlowCard } from "./FlowCard";
import { Rulebook } from "./Rulebook";

const flowCards = [
  {
    name: "call",
    title: "Test a Call",
    description:
      "Simulate a single call interaction to test AI response in real-time.",
    icon: <img height={48} width={48} src={callImg} />,
  },
  {
    name: "engagement",
    title: "Test an Engagement",
    description:
      "Run a series of follow-up calls based on call dispositions and other rules of the call flow.",
    icon: <img height={63} width={72} src={engagementImg} />,
    children: (
      <SheetTrigger asChild>
        <Flex className="mt-2 text-txt-brand-secondary items-center gap-2 cursor-pointer">
          <WrapText strokeWidth={1.5} className="text-inherit" />
          <p className="text-sm font-medium">View Rules of Call Flow</p>
        </Flex>
      </SheetTrigger>
    ),
  },
];

const getCTAText = (
  postMakeCallStatus: APIStateEnum | undefined,
  postMakeEngagementStatus: APIStateEnum | undefined,
  selection: ActivityType,
) => {
  if (selection === "call") {
    if (postMakeCallStatus === APIStateEnum.IN_PROGRESS) {
      return "Calling";
    }
    return "Send Call";
  }
  if (selection === "engagement") {
    if (postMakeEngagementStatus === APIStateEnum.IN_PROGRESS) {
      return "Starting Engagement";
    }
    return "Start Engagement";
  }
  return "Send Call";
};

const areRequiredFieldsFilled = (
  options: Record<string, any>,
  fields: any[],
) => {
  if (!fields || !options) {
    return false;
  }
  return fields.every(field => {
    if (!field.required) {
      return true;
    }
    const value = options[field.key];
    return value !== undefined && value !== null && value !== "";
  });
};

export default function Demo() {
  const { callFlowId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const [openAdvancedConfig, setOpenAdvancedConfig] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const advancedFormRef = useRef<HTMLFormElement>(null);
  const journey = useSelector((state: RootState) => state.journey.journey);
  const journeys = useSelector((state: RootState) => state.journey.journeys);
  const showFlowCards = Object.keys(journey?.ruleset?.rules || {}).length > 0;

  const [selection, setSelection] = useState<ActivityType>(
    flowCards[0].name as ActivityType,
  );

  const {
    latestActivity,
    postMakeCallStatus,
    postMakeEngagementStatus,
    callOptionsError,
    createActivityError,
  } = useSelector((state: RootState) => state.demo);
  const fetchJourneysStatus = useSelector(
    (state: RootState) => state.journey.fetchJourneysStatus,
  );
  const callOptions = useSelector((state: RootState) => state.demo.callOptions);
  const navigate = useNavigate();

  const screen = journey?.ui_schema;
  const error = callOptionsError || createActivityError?.toString();

  useEffect(() => {
    if (journeys.length === 0) {
      dispatch(fetchJourneys());
    }
    if (journeys.length && isEmpty(journey)) {
      const temp = journeys.find(r => r.id === callFlowId);
      dispatch(setJourney(temp));
    }
    return () => {
      dispatch(setJourney({}));
    };
  }, [dispatch, journeys.length]);

  const creationInProgress = [
    postMakeCallStatus,
    postMakeEngagementStatus,
  ].includes(APIStateEnum.IN_PROGRESS);

  useEffect(() => {
    return () => {
      dispatch(resetCallsState());
      dispatch(resetDemoState());
    };
  }, []);

  const handleFormDataChange = ({
    key,
    value,
  }: {
    key: string;
    value: any;
  }) => {
    const cloned = cloneDeep(callOptions);
    set(cloned, key, value);
    dispatch(setCallOptions(cloned));
  };

  const setDefaultData = (data: Record<string, any>) => {
    const cloned = cloneDeep(callOptions);
    const clone = { ...cloned, ...data };
    dispatch(setCallOptions(clone));
  };

  useEffect(() => {
    const fields = screen?.fields || [];
    const advancedFields = screen?.advancedFields || [];
    const allFields = [...fields, ...advancedFields];
    if (allFields.length === 0) {
      return;
    } else {
      const obj = {} as Record<string, any>;
      for (let i = 0; i < allFields.length; i++) {
        const row = allFields[i];

        if (row.type === "multidatetimeselectorfoodsmart") {
          const pastSlots = generateWeeklyDateTimeSlots({
            from: new Date(new Date().setMonth(new Date().getMonth() - 2)),
            to: new Date(),
            count: 3,
            timeConstraintTop: "08:00",
            timeConstraintBottom: "18:00",
          });
          const futureSlots = generateWeeklyDateTimeSlots({
            from: new Date(),
            to: new Date(new Date().setMonth(new Date().getMonth() + 1)),
            count: 2,
            timeConstraintTop: "08:00",
            timeConstraintBottom: "18:00",
          });
          const firstRdName =
            fields.filter(r => r.key === "slots.rd_name")?.[0]
              ?.dropdown_options?.[0] || availableDateSlotsConfig[0].name;

          set(obj, "slots", {
            past: [...pastSlots].map(d => moment(d).format("YYYY-MM-DD HH:mm")),
            future: [...futureSlots].map(d =>
              moment(d).format("YYYY-MM-DD HH:mm"),
            ),
            available: [
              ...generateTimeSlots(firstRdName).map(slot => {
                return moment(slot).format("YYYY-MM-DD HH:mm");
              }),
            ],
            rd_name: firstRdName as string,
          });
        } else if (row.type === "timezone-select") {
          set(obj, "timezone", getCompleteTimeZone());
        } else {
          if (row?.default_value !== null && row?.default_value !== undefined) {
            set(obj, row.key, row.default_value);
          }
        }
      }
      if (
        Object.keys(obj).length > 0 &&
        Object.keys(callOptions).length === 0
      ) {
        setDefaultData(obj);
      }
    }
  }, [callOptions, screen?.fields, screen?.advancedFields]);

  return (
    <Flex
      className="bg-bg-tertiary h-full rounded-2xl w-full relative  "
      direction="column"
      style={{
        maxHeight: "calc(100vh - 2rem )",
      }}
    >
      <Box className="border-b border-bd-secondary bg-bg-primary w-full rounded-t-2xl">
        <Flex className="py-6 px-8 w-full rounded-t-2xl">
          <Flex align="center" className="gap-4 pl-2">
            <Button
              variant="secondary-gray"
              className="p-2"
              onClick={() => window.history.back()}
            >
              <ArrowLeft />
            </Button>
            <p className="text-sm font-medium text-txt-tertiary">
              {journey?.display_name}
            </p>
          </Flex>
        </Flex>
      </Box>
      <Flex
        direction="column"
        className="rounded-b-2xl bg-bg-primary h-full
w-full overflow-scroll pb-20"
      >
        <div className="border rounded-xl border-utility-brand-50 mx-4 p-4 bg-gradient-to-r from-white via-white to-[var(--bg-brand-primary)]">
          <JourneyContext journey={journey} />
        </div>
        {fetchJourneysStatus === APIStateEnum.IN_PROGRESS ? (
          <Flex
            direction="column"
            className="w-full my-6 px-2 mx-auto max-w-screen-xl"
          >
            <Skeleton height="50vh" />
          </Flex>
        ) : (
          <Flex
            direction="column"
            className="w-full my-6 px-4 mx-auto max-w-4xl"
          >
            <Flex align="center" className="mb-5">
              <span className="rounded-md p-1.5 bg-bg-brand-primary shadow mr-2 border border-bd-primary">
                <Phone
                  height={16}
                  width={16}
                  strokeWidth="1.5"
                  className="text-txt-brand-primary"
                />
              </span>
              <p className="text-md font-medium text-txt-primary">
                {screen?.title}
              </p>
            </Flex>
            <Sheet>
              <Flex className="w-full gap-4 mb-4">
                {showFlowCards &&
                  flowCards.map(flowCard => (
                    <FlowCard
                      key={flowCard.name}
                      selection={selection}
                      disabled={creationInProgress}
                      onChange={value => {
                        setSelection(value as ActivityType);
                      }}
                      {...flowCard}
                    >
                      {flowCard.children}
                    </FlowCard>
                  ))}
              </Flex>
              <SheetContent className="w-full max-w-xl p-2 px-4 bg-bg-secondary">
                <Rulebook />
              </SheetContent>
            </Sheet>

            <Flex
              className="overflow-y-auto pr-4 w-full gap-6"
              direction="column"
            >
              <Flex className="border-b border-bd-secondary pb-6 w-full">
                <form
                  ref={formRef}
                  className="gap-2 grid grid-cols-2 w-full"
                  onSubmit={e => e.preventDefault()}
                >
                  {screen?.fields.map(field => (
                    <GetFieldForForm
                      field={field}
                      fields={screen?.fields}
                      data={callOptions}
                      key={field.key}
                      onDataChange={({
                        key,
                        value,
                      }: {
                        key: string;
                        value: any;
                      }) => {
                        handleFormDataChange({ key, value });
                      }}
                    />
                  ))}
                </form>
              </Flex>

              {!isEmpty(screen?.advancedFields) ? (
                <Flex direction="column">
                  <Flex
                    justify="between"
                    align="center"
                    onClick={() => setOpenAdvancedConfig(prev => !prev)}
                    className={`cursor-pointer relative ${
                      !openAdvancedConfig ? "mb-0" : "mb-8"
                    }`}
                  >
                    <p className="text-txt-primary">Configure Mock Data</p>
                    <NavArrowDown
                      height={24}
                      width={24}
                      strokeWidth={1.5}
                      className={`${
                        openAdvancedConfig ? "rotate-180" : ""
                      } transition-transform`}
                    />
                  </Flex>
                  <form
                    ref={advancedFormRef}
                    className={` ${
                      openAdvancedConfig
                        ? "visible space-y-4"
                        : "invisible h-1 mb-0 absolute"
                    }`}
                    onSubmit={e => e.preventDefault()}
                  >
                    {screen?.advancedFields?.map(field => (
                      <GetFieldForForm
                        key={field.key}
                        field={field}
                        fields={screen?.advancedFields}
                        data={callOptions}
                        onDataChange={({
                          key,
                          value,
                        }: {
                          key: string;
                          value: any;
                        }) => {
                          handleFormDataChange({ key, value });
                        }}
                        direction="row"
                      />
                    ))}
                  </form>
                </Flex>
              ) : (
                <></>
              )}
            </Flex>
          </Flex>
        )}
      </Flex>
      <div className="absolute bottom-0 right-0 bg-bg-secondary-hover w-full box-border p-2 rounded-b-xl shadow border-t border-bd-primary">
        <Flex
          justify={latestActivity?.id ? "between" : "end"}
          align="center"
          className="gap-4 w-full max-w-4xl mx-auto"
        >
          {!error && latestActivity?.id && latestActivity.type ? (
            <Link
              to={`/${
                latestActivity.type === "call" ? "call-logs" : "engagement-logs"
              }/details/${latestActivity.id}`}
            >
              <Button
                variant="link"
                className="hover:underline cursor-pointer text-txt-brand-secondary hover:text-txt-brand-primary"
                size="link"
              >
                View latest {latestActivity.type}
              </Button>
            </Link>
          ) : (
            ""
          )}
          {error && (
            <p className="text-txt-error-primary text-base my-auto">{error}</p>
          )}
          <Button
            className="w-max disabled:cursor-not-allowed"
            variant="primary"
            size="lg"
            disabled={
              creationInProgress ||
              isEmpty(callOptions) ||
              Boolean(error) ||
              !areRequiredFieldsFilled(callOptions, screen?.fields)
            }
            onClick={async event => {
              event.preventDefault();
              if (!formRef.current) {
                return;
              }
              const form = formRef.current;
              const currentSelection = selection;
              if (form.checkValidity()) {
                try {
                  const { id } = await dispatch(
                    currentSelection === "call"
                      ? postDemoCall()
                      : postDemoEngagement(),
                  ).unwrap();
                  if (id) {
                    const path = `/${currentSelection}-logs/details/${id}`;
                    navigate(path);
                  }
                  // else cases are handled inside the postDemo methods
                } catch (e) {
                  console.error("Error while making call/engagement", e);
                }
              }
            }}
          >
            <Flex className="gap-1" align="center">
              <PhoneOutcome className="w-5 h-5 text-txt-primary-on-brand" />
              <p className="font-semibold text-md">
                {getCTAText(
                  postMakeCallStatus,
                  postMakeEngagementStatus,
                  selection,
                )}
              </p>
            </Flex>
          </Button>
        </Flex>
      </div>
    </Flex>
  );
}
